// @givmo/board — the meeting's agenda: items, order, templates, consent placement.
//
// Three model facts drive this panel, and each would be easy to get wrong from UI instinct alone:
//
//   * ORDER IS EXPLICIT AND CONTIGUOUS. Reordering sends the WHOLE list of ids in the new order to
//     `PUT …/agenda-order`, which renumbers inside one transaction — its position constraint is
//     deferred precisely because two items exchanging positions are briefly equal under any ordering
//     of two updates. So the UI never sends a single item's new position.
//   * `is_consent_agenda` IS PLACEMENT, NOT TYPE. A report can ride the consent block without ceasing
//     to be a report, so it is a separate flag from `item_type` and the UI keeps them separate.
//   * TEMPLATES ARE A LIBRARY THE OFFICER CHOOSES FROM. `apply_agenda_template` takes an explicit
//     template id with NO default anywhere in the stack — selecting one from the organization's facts would
//     cross the §3.2 Band-2 line. So this panel always asks, never pre-selects, and never suggests one
//     based on the meeting. Sections arrive unpopulated and APPENDED, so the library stays usable
//     mid-draft.

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { extractList } from '@givmo/api-client/list';
import {
  isGivmoError,
  type BoardAgendaItemOut,
  type BoardAgendaItemType,
  type BoardAgendaTemplateOut,
} from '@givmo/api-client';
import {
  Button,
  Card,
  Checkbox,
  Dialog,
  ErrorMessage,
  Icon,
  RowActions,
  Select,
  TextField,
  Textarea,
  toast,
} from '@givmo/design-system';
import { ListChecks, Plus } from 'lucide-react';
import { ConnectedAiDraftBadge } from '../../components/connected-ai-draft-badge';
import { FieldNote } from '../../components/field-note';
import { useBoardOperations } from '../../board-runtime';
import { useBoardPermission } from '../../board-host';
import { keepRangesTogether } from './keep-ranges-together';

const ITEM_TYPE_OPTIONS: readonly { value: BoardAgendaItemType; label: string }[] = [
  { value: 'discussion', label: 'Discussion' },
  { value: 'motion_planned', label: 'Motion planned' },
  { value: 'report', label: 'Report' },
  { value: 'consent', label: 'Consent item' },
];

function itemTypeLabel(itemType: string | null | undefined): string {
  return (
    ITEM_TYPE_OPTIONS.find((option) => option.value === itemType)?.label ??
    (itemType ?? '').replace(/[_-]+/g, ' ')
  );
}

function extractAgendaItems(body: unknown): BoardAgendaItemOut[] {
  return [...extractList<BoardAgendaItemOut>(body, 'data')].sort(
    (left, right) => left.position - right.position,
  );
}

function extractTemplates(body: unknown): BoardAgendaTemplateOut[] {
  return extractList<BoardAgendaTemplateOut>(body, 'data');
}

export interface AgendaPanelProps {
  orgRef: string;
  meetingId: string;
  /**
   * A cancelled meeting's agenda is closed — the backend refuses every agenda write on it ("its agenda
   * stands as recorded") — so nothing to change it is offered. Absent, never disabled.
   */
  cancelled?: boolean;
}

/**
 * The agenda, one line per item (CBG-UX1, Jonathan's approved mockup m2): its number, its title, its
 * kind as a plain word (plus where it sits and how long it has), and — for someone who can change the
 * agenda — the row's "⋯" holding moving, editing and removing. Adding an item and starting from a
 * template sit in the card's header.
 */
export function AgendaPanel({ orgRef, meetingId, cancelled = false }: AgendaPanelProps) {
  const operations = useBoardOperations();
  const queryClient = useQueryClient();
  // Every change to the agenda is `board_meeting:write`; without it, the agenda is read-only.
  const canWrite = useBoardPermission('board_meeting:write') && !cancelled;
  const [editing, setEditing] = useState<BoardAgendaItemOut | null>(null);
  const [adding, setAdding] = useState(false);
  const [applyingTemplate, setApplyingTemplate] = useState(false);

  const itemsOptions = operations.officerListBoardAgendaItemsOptions({
    path: { org_ref: orgRef, meeting_id: meetingId },
  });
  const itemsQuery = useQuery({ ...itemsOptions, select: extractAgendaItems });
  const items = itemsQuery.data ?? [];

  const invalidateItems = () => queryClient.invalidateQueries({ queryKey: itemsOptions.queryKey });

  const reorder = useMutation({
    mutationFn: async (orderedIds: string[]) => {
      await operations.officerReorderBoardAgenda({
        path: { org_ref: orgRef, meeting_id: meetingId },
        body: { agenda_item_ids: orderedIds },
        throwOnError: true,
      });
    },
    onSuccess: () => void invalidateItems(),
    onError: (caught) => {
      toast.error(
        isGivmoError(caught) ? caught.message : 'We couldn’t reorder the agenda. Please try again.',
      );
    },
  });

  const removeItem = useMutation({
    mutationFn: async (item: BoardAgendaItemOut) => {
      await operations.officerDeleteBoardAgendaItem({
        path: { org_ref: orgRef, meeting_id: meetingId, agenda_item_id: item.id },
        throwOnError: true,
      });
    },
    onSuccess: async () => {
      await invalidateItems();
      toast.success('Item removed from the agenda.');
    },
    onError: (caught) => {
      toast.error(
        isGivmoError(caught) ? caught.message : 'We couldn’t remove that item. Please try again.',
      );
    },
  });

  /** Move an item one place and send the WHOLE resulting order (the renumber is transactional). */
  function move(index: number, direction: -1 | 1) {
    const target = index + direction;
    if (target < 0 || target >= items.length) return;
    const next = [...items];
    const moved = next[index];
    const displaced = next[target];
    if (moved === undefined || displaced === undefined) return;
    next[index] = displaced;
    next[target] = moved;
    reorder.mutate(next.map((item) => item.id));
  }

  // A cancelled meeting's agenda is kept as it stood, read-only — and when nothing was on it, there
  // is no card at all: "Nothing on the agenda yet" would promise a meeting that is not happening.
  // Hidden while the read is in flight too, so an empty agenda never flashes and disappears.
  if (cancelled && !(itemsQuery.isSuccess && items.length > 0)) return null;

  return (
    <Card testId="agenda-panel">
      <div className="flex flex-wrap items-center justify-between gap-12 border-b border-border-subtle px-20 py-14">
        <h2 className="font-sans text-s16 font-w600 tracking-givmo text-text-strong">Agenda</h2>
        {canWrite ? (
          <div className="flex items-center gap-8">
            <Button
              appearance="saas"
              tone="link"
              size="sm"
              className="mr-8"
              onClick={() => setApplyingTemplate(true)}
              testId="agenda-use-template"
            >
              Start from a template
            </Button>
            <Button
              appearance="saas"
              tone="secondary"
              size="sm"
              leadingIcon={<Icon icon={Plus} size={15} aria-hidden />}
              onClick={() => setAdding(true)}
              testId="agenda-add-item"
            >
              Add an item
            </Button>
          </div>
        ) : null}
      </div>

      {itemsQuery.isError ? (
        <div className="flex flex-col items-start gap-10 px-20 py-16" data-testid="agenda-error">
          <p className="font-sans text-s14 font-w400 tracking-givmo text-text-secondary">
            We couldn’t load this agenda.
          </p>
          <Button
            appearance="saas"
            tone="secondary"
            size="sm"
            onClick={() => void itemsQuery.refetch()}
            testId="agenda-error-retry"
          >
            Try again
          </Button>
        </div>
      ) : null}

      {itemsQuery.isSuccess && items.length === 0 ? (
        <p
          className="px-20 py-16 font-sans text-s14 font-w400 tracking-givmo text-text-secondary"
          data-testid="agenda-empty"
        >
          Nothing on the agenda yet.
        </p>
      ) : null}

      {items.length > 0 ? (
        <ol className="divide-y divide-border-subtle" data-testid="agenda-items">
          {items.map((item, index) => (
            <li
              key={item.id}
              className="flex items-center gap-12 px-20 py-12"
              data-testid={`agenda-item-${item.id}`}
            >
              <span className="w-20 shrink-0 font-sans text-s13 font-w600 tracking-givmo text-text-tertiary tabular-nums">
                {index + 1}
              </span>
              <span className="flex min-w-0 flex-1 flex-col gap-2 sm:flex-row sm:items-baseline sm:justify-between sm:gap-16">
                <span className="flex min-w-0 flex-col gap-2">
                  <span className="flex min-w-0 flex-wrap items-center gap-8">
                    <span className="min-w-0 font-sans text-s14 font-w500 tracking-givmo text-text-strong">
                      {item.title}
                    </span>
                    <ConnectedAiDraftBadge
                      source={item}
                      testId={`agenda-connected-ai-draft-${item.id}`}
                    />
                  </span>
                  {item.description ? (
                    // Its lines as written: an item added from a committee's decisions lists one
                    // decision per line (CBG-YR6-CONFIRM), and a typed description keeps its breaks.
                    <span className="whitespace-pre-line font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-tertiary">
                      {keepRangesTogether(item.description)}
                    </span>
                  ) : null}
                </span>
                <span
                  className="shrink-0 font-sans text-s13 font-w400 tracking-givmo text-text-tertiary"
                  data-testid={`agenda-item-kind-${item.id}`}
                >
                  {itemTypeLabel(item.item_type)}
                  {/* PLACEMENT, not type — a report can ride the consent block and stay a report. */}
                  {item.is_consent_agenda ? ' · on the consent agenda' : ''}
                  {item.time_box_minutes !== null && item.time_box_minutes !== undefined
                    ? ` · ${item.time_box_minutes} min`
                    : ''}
                </span>
              </span>
              {/* Moving, editing and removing are `board_meeting:write`: a reader sees the agenda
                  and nothing to act on. */}
              {canWrite ? (
                <RowActions
                  testId={`agenda-actions-${item.id}`}
                  label={`Actions for ${item.title}`}
                  actions={[
                    {
                      label: 'Move up',
                      testId: `agenda-move-up-${item.id}`,
                      disabled: index === 0 || reorder.isPending,
                      onSelect: () => move(index, -1),
                    },
                    {
                      label: 'Move down',
                      testId: `agenda-move-down-${item.id}`,
                      disabled: index === items.length - 1 || reorder.isPending,
                      onSelect: () => move(index, 1),
                    },
                    {
                      label: 'Edit this item',
                      testId: `agenda-edit-${item.id}`,
                      onSelect: () => setEditing(item),
                    },
                    {
                      label: 'Remove from the agenda',
                      testId: `agenda-remove-${item.id}`,
                      destructive: true,
                      onSelect: () => removeItem.mutate(item),
                    },
                  ]}
                />
              ) : null}
            </li>
          ))}
        </ol>
      ) : null}

      {adding || editing !== null ? (
        <AgendaItemDialog
          orgRef={orgRef}
          meetingId={meetingId}
          item={editing}
          onClose={() => {
            setAdding(false);
            setEditing(null);
          }}
          onSaved={() => {
            setAdding(false);
            setEditing(null);
            void invalidateItems();
          }}
        />
      ) : null}

      {applyingTemplate ? (
        <TemplatePickerDialog
          orgRef={orgRef}
          meetingId={meetingId}
          onClose={() => setApplyingTemplate(false)}
          onApplied={() => {
            setApplyingTemplate(false);
            void invalidateItems();
          }}
        />
      ) : null}
    </Card>
  );
}

function AgendaItemDialog({
  orgRef,
  meetingId,
  item,
  onClose,
  onSaved,
}: {
  orgRef: string;
  meetingId: string;
  item: BoardAgendaItemOut | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const operations = useBoardOperations();
  const isEdit = item !== null;
  const [title, setTitle] = useState(item?.title ?? '');
  const [description, setDescription] = useState(item?.description ?? '');
  const [itemType, setItemType] = useState<BoardAgendaItemType>(
    (item?.item_type as BoardAgendaItemType) ?? 'discussion',
  );
  const [onConsentAgenda, setOnConsentAgenda] = useState(item?.is_consent_agenda ?? false);
  const [timeBox, setTimeBox] = useState(
    item?.time_box_minutes !== null && item?.time_box_minutes !== undefined
      ? String(item.time_box_minutes)
      : '',
  );
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: async () => {
      const body = {
        title: title.trim(),
        description: description.trim().length > 0 ? description.trim() : null,
        item_type: itemType,
        is_consent_agenda: onConsentAgenda,
        time_box_minutes: /^\d+$/.test(timeBox.trim()) ? Number.parseInt(timeBox.trim(), 10) : null,
      };
      if (isEdit) {
        await operations.officerUpdateBoardAgendaItem({
          path: { org_ref: orgRef, meeting_id: meetingId, agenda_item_id: item.id },
          body,
          throwOnError: true,
        });
      } else {
        await operations.officerCreateBoardAgendaItem({
          path: { org_ref: orgRef, meeting_id: meetingId },
          body,
          throwOnError: true,
        });
      }
    },
    onSuccess: () => {
      toast.success(isEdit ? 'Agenda item updated.' : 'Added to the agenda.');
      onSaved();
    },
    onError: (caught) => {
      setError(isGivmoError(caught) ? caught.message : 'We couldn’t save that. Please try again.');
    },
  });

  return (
    <Dialog.Root open onOpenChange={(next) => !next && !save.isPending && onClose()}>
      <Dialog.Content
        aria-label={isEdit ? 'Edit agenda item' : 'Add an agenda item'}
        className="flex flex-col gap-16"
        testId="agenda-item-dialog"
      >
        <Dialog.Title className="font-sans text-s18 font-w700 tracking-givmo text-text-strong">
          {isEdit ? 'Edit this item' : 'Add an agenda item'}
        </Dialog.Title>

        <TextField
          label="Title"
          value={title}
          onChange={(event) => setTitle(event.target.value)}
          testId="agenda-item-title"
        />

        <Textarea
          label="Description (optional)"
          value={description}
          onChange={(event) => setDescription(event.target.value)}
          rows={3}
          testId="agenda-item-description"
        />

        <Select
          label="Kind of item"
          appearance="saas"
          value={itemType}
          onValueChange={(next) => next !== null && setItemType(next as BoardAgendaItemType)}
          options={ITEM_TYPE_OPTIONS.map((option) => ({
            value: option.value,
            label: option.label,
          }))}
          testId="agenda-item-type"
        />

        <label className="flex items-center gap-10 font-sans text-s14 font-w400 tracking-givmo text-text-secondary">
          <Checkbox
            appearance="saas"
            checked={onConsentAgenda}
            onCheckedChange={(next) => setOnConsentAgenda(next === true)}
            testId="agenda-item-consent"
          />
          Put this on the consent agenda
        </label>
        <FieldNote>
          Where the item sits in the meeting — it keeps whatever kind of item it is.
        </FieldNote>

        <TextField
          label="Time to allow, in minutes (optional)"
          inputMode="numeric"
          value={timeBox}
          onChange={(event) => setTimeBox(event.target.value)}
          testId="agenda-item-time-box"
        />

        {error !== null ? <ErrorMessage testId="agenda-item-error">{error}</ErrorMessage> : null}

        <div className="flex justify-end gap-10">
          <Button
            appearance="saas"
            tone="secondary"
            disabled={save.isPending}
            onClick={onClose}
            testId="agenda-item-cancel"
          >
            Cancel
          </Button>
          <Button
            appearance="saas"
            tone="primary"
            loading={save.isPending}
            disabled={title.trim().length === 0 || save.isPending}
            onClick={() => {
              setError(null);
              save.mutate();
            }}
            testId="agenda-item-save"
          >
            {isEdit ? 'Save changes' : 'Add it'}
          </Button>
        </div>
      </Dialog.Content>
    </Dialog.Root>
  );
}

function TemplatePickerDialog({
  orgRef,
  meetingId,
  onClose,
  onApplied,
}: {
  orgRef: string;
  meetingId: string;
  onClose: () => void;
  onApplied: () => void;
}) {
  const operations = useBoardOperations();
  // No default selection, ever. Choosing a template from the meeting's own facts is the §3.2 Band-2
  // line, so the officer picks or nothing happens.
  const [templateId, setTemplateId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const templatesQuery = useQuery({
    ...operations.officerListBoardAgendaTemplatesOptions({ path: { org_ref: orgRef } }),
    select: extractTemplates,
  });
  const templates = templatesQuery.data ?? [];
  const chosen = templates.find((template) => template.template_id === templateId) ?? null;

  const apply = useMutation({
    mutationFn: async () => {
      if (templateId === null) return;
      await operations.officerApplyBoardAgendaTemplate({
        path: { org_ref: orgRef, meeting_id: meetingId },
        body: { template_id: templateId },
        throwOnError: true,
      });
    },
    onSuccess: () => {
      toast.success('Template sections added to the agenda.');
      onApplied();
    },
    onError: (caught) => {
      setError(
        isGivmoError(caught)
          ? caught.message
          : 'We couldn’t apply that template. Please try again.',
      );
    },
  });

  return (
    <Dialog.Root open onOpenChange={(next) => !next && !apply.isPending && onClose()}>
      <Dialog.Content
        aria-label="Start from a template"
        className="flex flex-col gap-16"
        testId="agenda-template-dialog"
      >
        <Dialog.Title className="font-sans text-s18 font-w700 tracking-givmo text-text-strong">
          Start from a template
        </Dialog.Title>

        <p className="font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-secondary">
          These are neutral starting points, not recommendations — pick the one you want and edit it
          freely. Sections are added to the end of your agenda, so nothing already there is
          replaced.
        </p>

        {templatesQuery.isPending ? (
          <p
            className="font-sans text-s14 font-w400 tracking-givmo text-text-secondary"
            data-testid="agenda-template-loading"
          >
            Loading templates…
          </p>
        ) : null}

        {templates.length > 0 ? (
          <Select
            label="Template"
            appearance="saas"
            value={templateId ?? ''}
            onValueChange={(next) => setTemplateId(next !== null && next.length > 0 ? next : null)}
            options={[
              { value: '', label: 'Choose a template…' },
              ...templates.map((template) => ({
                value: template.template_id,
                label: template.name,
              })),
            ]}
            testId="agenda-template-select"
          />
        ) : null}

        {chosen !== null ? (
          <div className="flex flex-col gap-8" data-testid="agenda-template-preview">
            <p className="font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-tertiary">
              {chosen.provenance}
            </p>
            <ul className="flex flex-col gap-4 border-l border-border-subtle pl-16">
              {(chosen.items ?? []).map((section, index) => (
                <li
                  key={`${section.title}-${index}`}
                  className="flex items-center gap-8 font-sans text-s13 font-w400 tracking-givmo text-text-secondary"
                >
                  <Icon icon={ListChecks} size={14} className="text-icon-muted" aria-hidden />
                  {section.title}
                </li>
              ))}
            </ul>
          </div>
        ) : null}

        {error !== null ? (
          <ErrorMessage testId="agenda-template-error">{error}</ErrorMessage>
        ) : null}

        <div className="flex justify-end gap-10">
          <Button
            appearance="saas"
            tone="secondary"
            disabled={apply.isPending}
            onClick={onClose}
            testId="agenda-template-cancel"
          >
            Cancel
          </Button>
          <Button
            appearance="saas"
            tone="primary"
            loading={apply.isPending}
            disabled={templateId === null || apply.isPending}
            onClick={() => {
              setError(null);
              apply.mutate();
            }}
            testId="agenda-template-apply"
          >
            Add these sections
          </Button>
        </div>
      </Dialog.Content>
    </Dialog.Root>
  );
}
